import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Cart page (sections/jhilke-cart.liquid) without page reloads.
 *
 * A tap on minus, plus or Remove changes the count, the line total and the item count at once;
 * the request to /cart/change.js follows after a short pause, so a run of taps sends one request.
 * When every request has answered, the section is morphed from the last response, which brings
 * the totals, the shipping bar and any discount back in line with Shopify. If Shopify
 * refuses a change (stock, limits), that line goes back to its saved quantity with the reason.
 *
 * The links keep their /cart/change hrefs, so the page still works without JavaScript.
 *
 * Removing a line (Remove, or minus at one) is not sent straight away: the line shows "Removed …"
 * with an Undo button for UNDO_MS, and only then goes to Shopify (roadmap DO, 2026-10-08).
 *
 * When the cart has no discounts, the subtotal, shipping, free-shipping bar and total are painted
 * from the lines at once too, so a removed print leaves the totals straight away (2026-10-29).
 * With a discount, or while the sale tier bar is on, they wait for Shopify's answer, dimmed.
 */

const WAIT_MS = 400;
const UNDO_MS = 5000;

/** @type {Map<string, {quantity: number, timer: number | undefined}>} */
const waiting = new Map();
/** @type {Map<string, string>} */
const errors = new Map();
let inFlight = 0;
let latestSections = null;
let queue = Promise.resolve();

const root = () => document.querySelector('[data-jh-cart]');
const rowFor = (key) => [...document.querySelectorAll('.jh-cart-item[data-key]')].find((row) => row.dataset.key === key) || null;
const busy = () => waiting.size > 0 || inFlight > 0;

function statusRegion() {
  let region = document.getElementById('jh-cart-status');
  if (!region) {
    region = document.createElement('div');
    region.id = 'jh-cart-status';
    region.className = 'jh-cart-sr';
    region.setAttribute('aria-live', 'polite');
    document.body.append(region);
  }
  return region;
}

function announce(text) {
  const region = statusRegion();
  region.textContent = '';
  requestAnimationFrame(() => (region.textContent = text));
}

function money(cents) {
  const el = root();
  return formatMoney(cents, el?.dataset.moneyFormat || '{{amount}}', el?.dataset.currency || '');
}

function sectionIds() {
  const ids = new Set();
  const el = root();
  if (el?.dataset.sectionId) ids.add(el.dataset.sectionId);
  document.querySelectorAll('cart-items-component').forEach((c) => {
    if (c instanceof HTMLElement && c.dataset.sectionId) ids.add(c.dataset.sectionId);
  });
  return [...ids];
}

function visibleCount() {
  let count = 0;
  document.querySelectorAll('.jh-cart-item[data-key]').forEach((row) => {
    if (!row.hasAttribute('data-removing')) count += Number(row.dataset.qty) || 0;
  });
  return count;
}

function paintCount() {
  const count = visibleCount();
  const label = document.querySelector('[data-jh-cart-items]');
  if (label) label.innerHTML = label.innerHTML.replace(/^\s*\d+ items?/, `${count} item${count === 1 ? '' : 's'}`);
  // The header badge (sections/jhilke-header.liquid) also follows the cart event once Shopify answers.
  document.querySelectorAll('.jh-cart-badge[data-jh-cart-count]').forEach((badge) => (badge.textContent = String(count)));
  // the badge is aria-hidden; screen readers hear the count from the link's label (roadmap DO, 2026-10-07)
  document.querySelectorAll('[data-jh-cart-link]').forEach((link) => link.setAttribute('aria-label', `Cart, ${count} item${count === 1 ? '' : 's'}`));
  document.querySelectorAll('cart-icon').forEach((icon) => icon.renderCartBubble?.(count, false));
}

/** Paints the summary from the lines on screen. Only when no discount can change the sum. */
function paintTotals() {
  const summary = document.querySelector('[data-jh-cart-summary]');
  if (!(summary instanceof HTMLElement) || !summary.hasAttribute('data-jh-cart-local')) return;
  if (document.documentElement.dataset.jhTiers === 'on') return;
  let total = 0;
  document.querySelectorAll('.jh-cart-item[data-key]').forEach((row) => {
    total += (Number(row.dataset.unit) || 0) * (Number(row.dataset.qty) || 0);
  });
  const free = Number(summary.dataset.jhFree) || 0;
  const qualifies = total >= free;
  const setText = (selector, text) => summary.querySelectorAll(selector).forEach((el) => (el.textContent = text));
  setText('[data-jh-cart-subtotal]', money(total));
  setText('[data-jh-cart-total]', money(total));
  setText('[data-jh-cart-shipping]', qualifies ? 'Free' : money(Number(summary.dataset.jhShip) || 0));
  setText('[data-jh-cart-remaining]', money(Math.max(0, free - total)));
  summary.querySelectorAll('[data-jh-cart-progress]').forEach((el) => {
    if (el instanceof HTMLElement) el.style.width = `${free ? Math.min(100, Math.floor((total * 100) / free)) : 100}%`;
  });
  summary.querySelectorAll('[data-jh-cart-if-free]').forEach((el) => {
    if (el instanceof HTMLElement) el.hidden = !qualifies;
  });
  summary.querySelectorAll('[data-jh-cart-if-paid]').forEach((el) => {
    if (el instanceof HTMLElement) el.hidden = qualifies;
  });
  // the numbers are already right, so they are not dimmed while the request runs; the morph drops this
  summary.dataset.jhPainted = '1';
}

function setBusy(on) {
  document.querySelector('[data-jh-cart-summary]')?.setAttribute('aria-busy', on ? 'true' : 'false');
}

const nameOf = (row) => row.querySelector('.jh-cart-item-name')?.textContent.trim() || 'The print';

/** Swaps a line being removed for "Removed …" and an Undo button, until the removal is sent. */
function showUndo(row) {
  if (row.querySelector('[data-jh-cart-undo]')) return;
  const hadFocus = row.contains(document.activeElement);
  [...row.children].forEach((child) => {
    if (!(child instanceof HTMLElement)) return;
    child.dataset.jhUndoWasHidden = child.hidden ? '1' : '';
    child.hidden = true;
  });
  const strip = document.createElement('div');
  strip.setAttribute('data-jh-cart-undo', '');
  strip.style.cssText = 'grid-column:1/-1;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;';
  const text = document.createElement('span');
  text.textContent = `Removed ${nameOf(row)}.`;
  const button = document.createElement('button');
  button.type = 'button';
  button.setAttribute('data-jh-cart-undo-btn', '');
  button.setAttribute('aria-label', `Undo removing ${nameOf(row)}`);
  button.textContent = 'Undo';
  button.style.cssText = 'background:none;border:0;min-height:44px;padding:12px 8px;font:inherit;color:#C8962B;text-decoration:underline;cursor:pointer;';
  strip.append(text, button);
  row.append(strip);
  if (hadFocus) button.focus({ preventScroll: true });
  announce(`Removed ${nameOf(row)}. Undo is available for a few seconds.`);
}

function clearUndo(row) {
  const strip = row.querySelector('[data-jh-cart-undo]');
  if (!strip) return;
  strip.remove();
  [...row.children].forEach((child) => {
    if (!(child instanceof HTMLElement)) return;
    child.hidden = child.dataset.jhUndoWasHidden === '1';
    delete child.dataset.jhUndoWasHidden;
  });
}

/** Undo: drops the waiting removal and puts the line back at the quantity Shopify last confirmed. */
function undo(row) {
  const entry = waiting.get(row.dataset.key);
  if (entry) {
    clearTimeout(entry.timer);
    waiting.delete(row.dataset.key);
  }
  paintRow(row, Number(row.dataset.saved ?? 1) || 1);
  row.querySelector('.jh-cart-remove')?.focus({ preventScroll: true });
  announce(`${nameOf(row)} is back in your cart.`);
  if (!busy()) settle();
}

/** Paints a quantity on the row straight away. */
function paintRow(row, quantity) {
  row.dataset.qty = String(quantity);
  const error = row.querySelector('[data-jh-cart-error]');
  if (error) error.hidden = true;
  if (quantity === 0) {
    showUndo(row);
  } else {
    clearUndo(row);
    row.removeAttribute('data-removing');
    row.hidden = false;
    const qty = row.querySelector('[data-jh-cart-qty]');
    if (qty) qty.textContent = String(quantity);
    const total = row.querySelector('[data-jh-cart-line-total]');
    if (total) total.textContent = money((Number(row.dataset.unit) || 0) * quantity);
  }
  paintCount();
  paintTotals();
}

function showErrors() {
  errors.forEach((message, key) => {
    const row = rowFor(key);
    const slot = row?.querySelector('[data-jh-cart-error]');
    if (slot) {
      slot.textContent = message;
      slot.hidden = false;
    }
  });
  errors.clear();
}

async function settle() {
  if (busy()) return;
  setBusy(false);
  const sections = latestSections;
  latestSections = null;
  const id = root()?.dataset.sectionId;
  if (sections && id && sections[id]) {
    try {
      await morphSection(id, sections[id], { mode: 'full' });
    } catch (error) {
      console.warn('[jhilke-cart-live] morph failed, reloading', error);
      window.location.reload();
      return;
    }
  }
  showErrors();
}

/** Puts a row back to the quantity Shopify last confirmed. */
function revert(row, savedQuantity, message) {
  paintRow(row, savedQuantity);
  errors.set(row.dataset.key, message);
}

async function send(key, quantity) {
  const row = rowFor(key);
  const saved = row ? Number(row.dataset.saved ?? row.dataset.qty) : 0;
  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: key, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(
      (window.Shopify?.routes?.root || '/') + 'cart/change.js',
      fetchConfig('json', { body: JSON.stringify({ id: key, quantity, sections: ids.join(','), sections_url: window.location.pathname }) })
    );
    const json = await response.json();
    if (!response.ok || json.status || json.errors) {
      const message = json.description || json.message || 'That change did not go through.';
      throw Object.assign(new Error(message), { refused: true });
    }
    if (row) row.dataset.saved = String(quantity);
    if (row && quantity === 0) row.hidden = true;
    latestSections = json.sections || latestSections;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse?.(json),
      detail: { sections: json.sections, items: json.items, itemCount: json.item_count, source: 'jhilke-cart-live', didError: false },
    });
    announce(quantity > 0 ? `Quantity updated to ${quantity}.` : 'Item removed.');
  } catch (error) {
    deferred.reject(error);
    document.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Cart update failed', code: error?.refused ? 'INVALID' : 'SERVICE_UNAVAILABLE' }));
    const message = error?.refused ? error.message : 'Your cart did not update. Check your connection and try again.';
    if (row) revert(row, saved, message);
    announce(message);
  }
}

function schedule(key, quantity) {
  const entry = waiting.get(key);
  if (entry) clearTimeout(entry.timer);
  const timer = window.setTimeout(() => {
    waiting.delete(key);
    inFlight += 1;
    queue = queue
      .then(() => send(key, quantity))
      .finally(() => {
        inFlight -= 1;
        settle();
      });
  }, quantity === 0 ? UNDO_MS : WAIT_MS);
  waiting.set(key, { quantity, timer });
  setBusy(true);
}

if (!window.__jhCartLive) {
  window.__jhCartLive = true;

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const undoButton = event.target instanceof Element ? event.target.closest('[data-jh-cart-undo-btn]') : null;
    const undoRow = undoButton?.closest('.jh-cart-item[data-key]');
    if (undoRow instanceof HTMLElement) {
      event.preventDefault();
      undo(undoRow);
      return;
    }
    const target = event.target instanceof Element ? event.target.closest('[data-jh-cart-step], [data-jh-cart-set]') : null;
    const row = target?.closest('.jh-cart-item[data-key]');
    if (!target || !(row instanceof HTMLElement) || !root()?.contains(row)) return;
    event.preventDefault();
    if (row.dataset.saved == null) row.dataset.saved = row.dataset.qty;
    const current = Number(row.dataset.qty) || 0;
    const next = target.hasAttribute('data-jh-cart-set')
      ? Number(target.getAttribute('data-jh-cart-set'))
      : Math.max(0, current + Number(target.getAttribute('data-jh-cart-step')));
    if (next === current) return;
    paintRow(row, next);
    schedule(row.dataset.key, next);
  });

  // A removal waits UNDO_MS before it is sent. Checkout must not start before it is sent, or the print
  // the shopper removed would still be in the order: send everything waiting, then submit.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement) || !root()?.contains(form) || form.dataset.jhFlushed) return;
    if (!waiting.size && !inFlight) return;
    event.preventDefault();
    const submitter = event.submitter;
    const updates = {};
    waiting.forEach(({ quantity, timer }, key) => {
      clearTimeout(timer);
      updates[key] = quantity;
    });
    waiting.clear();
    try {
      await queue;
      if (Object.keys(updates).length) {
        await fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', fetchConfig('json', { body: JSON.stringify({ updates }) }));
      }
    } catch (error) {
      console.warn('[jhilke-cart-live] could not send the waiting changes before checkout', error);
    }
    form.dataset.jhFlushed = '1';
    if (submitter && submitter.name) {
      // keep the clicked button's name (checkout) in the submission
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = submitter.name;
      field.value = submitter.value || '';
      form.append(field);
    }
    form.submit();
  });

  window.addEventListener('pagehide', () => {
    // Send anything still waiting so a quick tap before leaving is not lost.
    waiting.forEach(({ quantity, timer }, key) => {
      clearTimeout(timer);
      const body = new Blob([JSON.stringify({ id: key, quantity })], { type: 'application/json' });
      navigator.sendBeacon?.((window.Shopify?.routes?.root || '/') + 'cart/change.js', body);
    });
    waiting.clear();
  });
}

/** Lets the related-prints quick add refresh this page without a reload. */
window.jhCartLive = {
  sectionIds,
  apply(sections, message) {
    latestSections = sections || latestSections;
    if (message) announce(message);
    settle();
  },
};
